/**
 * The counter fleet's release record, and the manifest the founder signs
 * offline (docs/plans/handheld-fleet.html 2, 3, 6.4, 16.1).
 *
 * Two shapes live here rather than in `internal/index.ts`, and the reason is
 * that only ONE of them is a wire shape at all. `HandheldReleaseManifest` is a
 * DOCUMENT: its exact bytes are what the private key signed and what a counter
 * verifies against a public key baked into the running app, so it is parsed
 * here on the way in and never re-emitted. A schema that lived beside the
 * endpoint that happens to receive it today would be restated the moment the
 * device-facing offer (plan 3) needs the same fields - and the two copies would
 * then disagree about a document whose whole value is that nobody can change it.
 *
 * `.strictObject` throughout, deliberately. A manifest carrying a field this
 * schema does not know is not a manifest this platform produced, and the answer
 * to that is a refusal rather than a shrug: the signature covers the bytes, so
 * an unknown field is either a version of the format nobody here understands or
 * an attempt to smuggle one past the checks below.
 */

import { z } from 'zod';
import { Instant, RemoteObjectUrl, Uuid } from './common';

/** The package a counter runs. A literal, because there is exactly one. */
export const HANDHELD_APPLICATION_ID = 'com.croissantinbed.handheld';

/**
 * Which world a build is for.
 *
 * Inside the signature on purpose: a demo build installed at a real counter
 * would talk to the demo API with a production device row, and the manifest is
 * the only place that fact can be stated where nobody at the platform can
 * change it afterwards. The worker refuses a manifest whose channel is not its
 * own (`isProduction(env) ? 'production' : 'demo'`).
 */
export const HandheldReleaseChannel = z.enum(['demo', 'production']);
export type HandheldReleaseChannel = z.infer<typeof HandheldReleaseChannel>;

/**
 * Which of the two offline keys signed a manifest.
 *
 * Two keys, held in two places, because Android 7.1 has no signing-key rotation:
 * the reserve is the fleet's only recovery from a leak of the primary (plan
 * 6.4, and the never-table's "one release-signing keypair instead of two").
 */
export const HandheldSigningKeyId = z.enum(['primary', 'reserve']);
export type HandheldSigningKeyId = z.infer<typeof HandheldSigningKeyId>;

/** A lower-case hex SHA-256, as the platform computes it from the APK bytes. */
export const Sha256Hex = z.string().regex(/^[0-9a-f]{64}$/);

/**
 * The signed document.
 *
 * `sha256` and `sizeBytes` are the APK's, computed by the worker from the bytes
 * it stored and NEVER typed by an operator - a hash somebody types is a hash
 * that says whatever they were told to type. The founder signs what the
 * platform displayed; the device downloads, hashes and compares.
 */
export const HandheldReleaseManifest = z.strictObject({
  applicationId: z.literal(HANDHELD_APPLICATION_ID),
  versionCode: z.number().int().min(1).max(2_100_000_000),
  versionName: z.string().min(1).max(40),
  sha256: Sha256Hex,
  sizeBytes: z.number().int().min(1),
  channel: HandheldReleaseChannel,
  signedAt: Instant,
});
export type HandheldReleaseManifest = z.infer<typeof HandheldReleaseManifest>;

/**
 * What the platform keeps of an offline signing act.
 *
 * `manifestJson` is the exact string that was signed, kept verbatim, because
 * the device verifies bytes and not a re-serialization of them: reordering one
 * key or dropping one space produces a document the signature no longer covers,
 * and the failure would arrive at a bakery rather than here.
 */
export const HandheldReleaseSignature = z.strictObject({
  manifestJson: z.string().min(2).max(2048),
  /** Base64 RSASSA-PKCS1-v1_5 over SHA-256 - `SHA256withRSA` on the device. */
  signature: z.string().min(64).max(1024),
  keyId: HandheldSigningKeyId,
  /** When the platform recorded the signature, on the platform clock. */
  signedAt: Instant,
});
export type HandheldReleaseSignature = z.infer<typeof HandheldReleaseSignature>;

/**
 * One uploaded build: six facts and the KEY of a file (plan 2).
 *
 * No row anywhere holds the bytes - the same rule every image on this platform
 * already obeys - and the object lives in the FLEET bucket rather than MEDIA,
 * because `GET /media/*` is unauthenticated and serves every prefix outside the
 * signed ones (plan 16.4).
 */
export const HandheldRelease = z.strictObject({
  versionCode: z.number().int().min(1).max(2_100_000_000),
  versionName: z.string().min(1).max(40),
  apkKey: z.string().min(1).max(200),
  sha256: Sha256Hex,
  sizeBytes: z.number().int().min(1),
  uploadedAt: Instant,
  uploadedBy: z.string().min(1).max(200),
  /** Null until the founder has signed the manifest offline and pasted it back. */
  signature: HandheldReleaseSignature.nullable(),
  publishedAt: Instant.nullable(),
  publishedBy: z.string().max(200).nullable(),
  /** Set when a rollout was stopped on this build (plan 12); never cleared. */
  haltedAt: Instant.nullable(),
});
export type HandheldRelease = z.infer<typeof HandheldRelease>;

/**
 * Who a published release is OFFERED to (plan 12).
 *
 * Ring 0 is the canary shops and nobody else, which is what a publish resets to
 * - a build reaches the fleet by being widened, never by being published. The
 * percent is the rest of the estate, and it is the one thing a force reads
 * before it is allowed to raise the minimum: forcing a build most of the fleet
 * was never offered blocks counters on a release they cannot have.
 */
export const HandheldRollout = z.strictObject({
  canaryLocationIds: z.array(Uuid).max(200),
  percent: z.number().int().min(0).max(100),
});
export type HandheldRollout = z.infer<typeof HandheldRollout>;

/**
 * The ONE singleton the update offer is computed from (plan 2).
 *
 * Forcing IS raising the minimum: there is no per-device force and no second
 * mechanism. A device below `minimumVersionCode` is forced, one at or above it
 * is not, and Remove force is the same write in reverse.
 *
 * `blocksAt` sits OUTSIDE the signature on purpose (plan 17): a grace window is
 * an operational decision made at 07:00, and putting it inside would mean the
 * founder signs offline for every change to it.
 */
export const HandheldUpdateState = z.strictObject({
  /** Null until the first publish. */
  latestVersionCode: z.number().int().min(1).max(2_100_000_000).nullable(),
  /** 0 until the first force - the state in which nothing is ever blocked. */
  minimumVersionCode: z.number().int().min(0).max(2_100_000_000),
  blocksAt: Instant.nullable(),
  forcedAt: Instant.nullable(),
  forcedBy: z.string().max(200).nullable(),
  rollout: HandheldRollout,
});
export type HandheldUpdateState = z.infer<typeof HandheldUpdateState>;

/** The administered canary list a publish resets ring 0 to. */
export const HandheldCanaryLocations = z.strictObject({
  locationIds: z.array(Uuid).max(200),
});
export type HandheldCanaryLocations = z.infer<typeof HandheldCanaryLocations>;

/**
 * Every alert the RELEASE CHANNEL raises - about a build, never about a device.
 *
 * A separate list from `HANDHELD_ALERT_KINDS` (handheld-telemetry.ts) because
 * they answer to different owners and different readers: those are one
 * counter's own trouble, these are the fleet's. They are here, in contracts,
 * for the same reason the other list is - `apps/internal-admin` owes every one
 * of them an `activity.alert.<KIND>` title in both catalogues and cannot import
 * the worker, so the completeness test walks THIS list and a fourth kind added
 * without copy is a build failure rather than `HANDHELD_ROLLOUT_HALTED` printed
 * into a table cell.
 */
export const HANDHELD_RELEASE_ALERT_KINDS = [
  'HANDHELD_RELEASE_PUBLISHED',
  'HANDHELD_FORCE_SET',
  'HANDHELD_ROLLOUT_HALTED',
] as const;

/**
 * Every audit action the release channel writes, for the same reason.
 *
 * The subject is `handheld_release` for the six about a build and
 * `handheld_update` for the three about the singleton; the titles are one
 * family and are asserted as one.
 */
export const HANDHELD_RELEASE_AUDIT_ACTIONS = [
  'handheld.release_uploaded',
  'handheld.release_signed',
  'handheld.release_published',
  'handheld.release_downloaded',
  'handheld.release_halted',
  'handheld.release_unhalted',
  'handheld.rollout_widened',
  'handheld.force_set',
  'handheld.force_cleared',
] as const;

/**
 * The path parameter every single-release route takes, on BOTH mounts.
 *
 * Coerced to a strict positive integer here, at the boundary, because the R2
 * key is composed from the INTEGER and the raw segment is discarded (plan
 * 16.4). A key built by concatenating a path segment is how a route reads
 * objects it was never meant to - and `/b/app/releases/../catalogue/x/apk` is
 * exactly the request that would do it, refused here before any handler runs.
 *
 * One definition rather than one per mount: the operator routes under
 * `/internal` and the device's download under `/b` address the same build by
 * the same number, and a second copy is a second chance for one of them to stop
 * coercing.
 */
export const HandheldVersionCodeParam = z.object({
  versionCode: z.coerce.number().int().min(1).max(2_100_000_000),
});
export type HandheldVersionCodeParam = z.infer<typeof HandheldVersionCodeParam>;

/**
 * What a counter is offered on its own heartbeat (plan 3).
 *
 * Everything the device ACTS on is inside `manifestJson`, which travels as the
 * exact string the offline key signed: the device verifies the signature over
 * those bytes and then trusts nothing else about this object. Re-serializing
 * the manifest on either side is how the bytes drift, and a drifted byte is a
 * signature failure at a bakery rather than here.
 *
 * The version and the hash are inside the signature and the URL is not, and
 * that split is the whole design. A compromised worker can point `apkUrl`
 * anywhere it likes; the device downloads from there and then discards the
 * bytes, because their SHA-256 is not the one the offline key signed. What an
 * attacker who owns the platform can do is withhold updates, replay an old
 * signed manifest, or blockade counters with `blocksAt`. What they cannot do is
 * install code (plan 16.1, 17).
 */
export const HandheldUpdateOffer = z.object({
  /** The signed bytes, verbatim. Parsed by the device only AFTER `signature` verifies. */
  manifestJson: z.string().min(2).max(2048),
  /** Base64 SHA256withRSA over the UTF-8 bytes of `manifestJson`, by the offline key. */
  signature: z.string().min(64).max(1024),
  /** Which baked-in trust anchor signed it: 'primary' or 'reserve' (plan 16.1). */
  keyId: HandheldSigningKeyId,
  /**
   * Composed server-side from `baseUrl(env, 'api')` - never assembled by a
   * device, and never a bucket URL: a public R2 address loses attribution,
   * revocation and environment separation, and writes down a host that
   * `PLATFORM_DOMAIN` does not govern (plan 3).
   */
  apkUrl: RemoteObjectUrl,
  /**
   * Null: the offer is dismissible. An instant: the release is forced and
   * blocking begins then (the grace window); an instant in the past means
   * blocking now. Judged on the platform clock the device already keeps, which
   * is why the platform sends the instant either way rather than a boolean it
   * has computed for one beat.
   *
   * DELIBERATELY OUTSIDE the manifest: it is an operational decision the
   * platform makes and re-makes, not a property of the build. It can therefore
   * only ever change WHEN a verified build is offered, never WHICH one.
   */
  blocksAt: Instant.nullable(),
});
export type HandheldUpdateOffer = z.infer<typeof HandheldUpdateOffer>;
